import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { PERMANENT_REDIRECTS } from "./lib/permanent-redirects";

// 301-redirect any uppercase URL to its lowercase equivalent
// (also enforces canonical: /Blog -> /blog, /Residential-Plumbing-Phoenix -> /residential-plumbing-phoenix).
const canonicalUrlMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  // Skip server-internal/asset paths so we never break server fns or assets.
  if (
    url.pathname.startsWith("/_serverFn") ||
    url.pathname.startsWith("/_server") ||
    url.pathname.startsWith("/assets/") ||
    url.pathname.startsWith("/api/")
  ) {
    return next();
  }

  let redirect = false;

  // Enforce canonical host: www.phoenixplumbing.co
  const host = url.host.toLowerCase();
  if (host === "phoenixplumbing.co") {
    url.host = "www.phoenixplumbing.co";
    url.protocol = "https:";
    redirect = true;
  }
  // Enforce https when host is the canonical production host
  if (url.host === "www.phoenixplumbing.co" && url.protocol !== "https:") {
    url.protocol = "https:";
    redirect = true;
  }

  // Enforce lowercase URLs
  if (url.pathname !== "/" && /[A-Z]/.test(url.pathname)) {
    url.pathname = url.pathname.toLowerCase();
    redirect = true;
  }

  // Strip trailing slash (except root)
  if (url.pathname.length > 1 && url.pathname.endsWith("/")) {
    url.pathname = url.pathname.replace(/\/+$/, "");
    redirect = true;
  }

  const retiredTarget = PERMANENT_REDIRECTS[url.pathname];
  if (retiredTarget) {
    url.pathname = retiredTarget;
    redirect = true;
  }

  if (redirect) {
    return new Response(null, {
      status: 301,
      headers: { Location: url.toString() },
    });
  }
  return next();
});

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [canonicalUrlMiddleware, errorMiddleware],
}));
